import React from 'react';
import { Shield, Save, Check, X } from 'lucide-react';
import { PERMISSION_STRUCTURE } from '../hooks/useRoleForm';

const ACTION_COLORS = {
  View:   { color: '#10b981', bg: '#f0fdf4', border: '#a7f3d0' },
  Add:    { color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
  Edit:   { color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
  Delete: { color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
};

/**
 * Shared Role form — role name input + full permissions editor.
 * Used by both Add and Edit.
 */
const RoleForm = ({
  formData,
  handleNameChange,
  handleDescriptionChange,
  togglePermission,
  handleQuickAction,
  isSaving,
  onSave,
  showSaveButton = true,
}) => (
  <div>
    {/* Top Card — name, description, quick actions, save button */}
    <div style={{
      background: '#fff', borderRadius: '16px',
      border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
      padding: '24px', marginBottom: '20px'
    }}>
      {/* Save button row */}
      {showSaveButton && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <span style={{ fontSize: '13px', color: '#64748b' }}>
            <strong style={{ color: '#10b981', fontSize: '16px' }}>{formData.permissions.length}</strong> permissions selected
          </span>
          <button
            onClick={onSave}
            disabled={isSaving}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: isSaving ? '#6ee7b7' : '#10b981', color: '#fff',
              border: 'none', borderRadius: '10px', padding: '10px 22px',
              fontSize: '14px', fontWeight: 700, cursor: isSaving ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 14px rgba(16,185,129,0.2)', transition: 'background 0.2s'
            }}
          >
            <Save style={{ width: 16, height: 16 }} />
            {isSaving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* Role name */}
      <div style={{ marginBottom: '16px', maxWidth: '400px' }}>
        <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
          ROLE NAME <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <div style={{ position: 'relative' }}>
          <Shield style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8' }} />
          <input
            type="text"
            value={formData.name}
            onChange={handleNameChange}
            placeholder="e.g. Nurse, OT Manager"
            style={{
              width: '100%', padding: '12px 14px 12px 42px',
              border: '1.5px solid #e2e8f0', borderRadius: '12px',
              fontSize: '14px', fontWeight: 600, color: '#0f172a',
              background: '#fff', outline: 'none', boxSizing: 'border-box',
            }}
            onFocus={e => e.target.style.borderColor = '#10b981'}
            onBlur={e => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>
      </div>

      {/* Description */}
      <div style={{ marginBottom: '20px', maxWidth: '600px' }}>
        <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
          DESCRIPTION
        </label>
        <textarea
          value={formData.description}
          onChange={handleDescriptionChange}
          rows={2}
          placeholder="Optional description for this role"
          style={{
            width: '100%', padding: '12px 14px',
            border: '1.5px solid #e2e8f0', borderRadius: '12px',
            fontSize: '13px', color: '#374151', background: '#fff',
            outline: 'none', resize: 'vertical', boxSizing: 'border-box',
          }}
          onFocus={e => e.target.style.borderColor = '#10b981'}
          onBlur={e => e.target.style.borderColor = '#e2e8f0'}
        />
      </div>

      {/* Quick actions */}
      <div>
        <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
          QUICK ACTIONS:
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {['View', 'Add', 'Edit', 'Delete'].map(action => (
            <button key={action} onClick={() => handleQuickAction(action)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', fontSize: '13px', fontWeight: 700, background: ACTION_COLORS[action].bg, color: ACTION_COLORS[action].color, border: `1px solid ${ACTION_COLORS[action].border}`, borderRadius: '8px', cursor: 'pointer' }}>
              <Check style={{ width: 13, height: 13 }} /> All {action}
            </button>
          ))}
          <div style={{ width: 1, background: '#e2e8f0', margin: '0 4px', alignSelf: 'stretch' }} />
          <button onClick={() => handleQuickAction('Check All')} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', fontSize: '13px', fontWeight: 700, background: '#f0fdf4', color: '#10b981', border: '1px solid #a7f3d0', borderRadius: '8px', cursor: 'pointer' }}>
            <Check style={{ width: 13, height: 13 }} /> Check All
          </button>
          <button onClick={() => handleQuickAction('Uncheck All')} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', fontSize: '13px', fontWeight: 700, background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer' }}>
            <X style={{ width: 13, height: 13 }} /> Uncheck All
          </button>
        </div>
      </div>
    </div>

    {/* Permission Cards */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingBottom: '32px' }}>
      {PERMISSION_STRUCTURE.map(category => (
        <div key={category.category} style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          <div style={{ padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
            <h4 style={{ margin: 0, fontSize: '11px', fontWeight: 800, color: '#1e293b', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              {category.category}
            </h4>
          </div>
          <div>
            {category.subsections.map((sub, subIdx) => (
              <div key={sub.name} style={{
                padding: '16px 20px',
                borderBottom: subIdx < category.subsections.length - 1 ? '1px solid #f8fafc' : 'none',
                display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap'
              }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#10b981', letterSpacing: '0.08em', textTransform: 'uppercase', minWidth: '200px' }}>
                  {sub.name}
                </span>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  {sub.actions.map(action => {
                    const permKey = `${sub.key} ${action}`;
                    const isChecked = formData.permissions.includes(permKey);
                    const ac = ACTION_COLORS[action] || ACTION_COLORS.View;
                    return (
                      <div key={action} onClick={() => togglePermission(permKey)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
                        <div style={{
                          width: 20, height: 20, borderRadius: '6px',
                          border: `2px solid ${isChecked ? ac.color : '#d1d5db'}`,
                          background: isChecked ? ac.color : '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.15s', flexShrink: 0
                        }}>
                          {isChecked && <Check style={{ width: 12, height: 12, color: '#fff', strokeWidth: 3 }} />}
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: isChecked ? 700 : 500, color: isChecked ? '#1e293b' : '#94a3b8', transition: 'color 0.15s' }}>
                          {action}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default RoleForm;
